import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { JobCard } from "@/components/JobCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRegion } from "@/context/RegionContext";
import { FunnelSimple } from "@phosphor-icons/react";

export default function Jobs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { region, meta } = useRegion();

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ categories: [], job_types: [] });
  const [savedIds, setSavedIds] = useState([]);

  const q = searchParams.get("q") || "";
  const location = searchParams.get("location") || "";
  const category = searchParams.get("category") || "";
  const jobType = searchParams.get("job_type") || "";

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs", {
        params: { q, location, category, job_type: jobType, country: region, page, limit: 15 },
      });
      setJobs(data.jobs);
      setTotal(data.total);
      setCapped(!!data.capped);
    } finally {
      setLoading(false);
    }
  }, [q, location, category, jobType, region, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { setPage(1); }, [q, location, category, jobType, region]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  useEffect(() => {
    api.get("/jobs/filters", { params: { country: region } }).then(({ data }) => setFilters(data)).catch(() => {});
  }, [region]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/me/saved-jobs").then(({ data }) => setSavedIds(data.saved_ids)).catch(() => {});
    }
  }, [user]);

  const onToggleSaved = (id, isSaved) =>
    setSavedIds((prev) => (isSaved ? [...prev, id] : prev.filter((x) => x !== id)));

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const clearFilters = () => setSearchParams(q ? new URLSearchParams({ q }) : new URLSearchParams());

  const totalPages = Math.max(1, Math.ceil(total / 15));

  const FilterPanel = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold">Filters</h3>
        <button onClick={clearFilters} className="text-xs font-medium text-primary hover:underline" data-testid="clear-filters-btn">
          Clear all
        </button>
      </div>

      <div>
        <p className="mb-3 text-sm font-semibold">Job type</p>
        <div className="space-y-2.5">
          {filters.job_types.length === 0 && <p className="text-xs text-muted-foreground">No options</p>}
          {filters.job_types.map((t) => (
            <label key={t} className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground/80" data-testid={`filter-jobtype-${t}`}>
              <Checkbox
                checked={jobType === t}
                onCheckedChange={(c) => setParam("job_type", c ? t : "")}
              />
              {t}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 text-sm font-semibold">Category</p>
        <div className="max-h-56 space-y-2.5 overflow-y-auto">
          {filters.categories.length === 0 && <p className="text-xs text-muted-foreground">No options</p>}
          {filters.categories.map((c) => (
            <label key={c} className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground/80" data-testid={`filter-category-${c}`}>
              <Checkbox
                checked={category === c}
                onCheckedChange={(ch) => setParam("category", ch ? c : "")}
              />
              {c}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="App bg-secondary/40">
      <Navbar />

      <div className="border-b border-border bg-navy py-8">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <SearchBar
            categories={filters.categories}
            initial={{ q, location, category: category || "all" }}
          />
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold tracking-tight md:text-3xl" data-testid="jobs-heading">
            {q ? `${q} jobs` : "All jobs"}{location ? ` in ${location}` : ""}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="jobs-count">
            {loading ? "Searching…" : `${total.toLocaleString()}${capped ? "+" : ""} job${total !== 1 ? "s" : ""} found in the ${meta.label}`}
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-border bg-card p-5">
              <FilterPanel />
            </div>
          </aside>

          {/* Mobile filter toggle handled inline */}
          <div className="min-w-0">
            <details className="mb-4 rounded-2xl border border-border bg-card p-4 lg:hidden">
              <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                <FunnelSimple size={18} /> Filters
              </summary>
              <div className="mt-4"><FilterPanel /></div>
            </details>

            <div className="space-y-3">
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-28 w-full rounded-xl" />
                ))}

              {!loading && jobs.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border bg-card p-16 text-center" data-testid="no-jobs-message">
                  <p className="font-semibold">No jobs found</p>
                  <p className="mt-1 text-sm text-muted-foreground">Try adjusting your filters or search terms.</p>
                  <Button variant="outline" className="mt-4 rounded-full" onClick={clearFilters}>Clear filters</Button>
                </div>
              )}

              {!loading &&
                jobs.map((job, i) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    index={i}
                    saved={savedIds.includes(job.id)}
                    onToggleSaved={onToggleSaved}
                  />
                ))}
            </div>

            {!loading && totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-3">
                <Button
                  variant="outline" className="rounded-full" disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button
                  variant="outline" className="rounded-full" disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
